import React, {useCallback, useMemo, useState} from 'react';
import cx from 'classnames';
import {
    Text,
    ContextMenu,
    ContextMenuItem,
    Icon,
    PriorityIcon
} from '@priority-software/priority-style-react';
import usePortal from 'react-useportal';
import {isRTL} from '../../Localization';
import {formsStore} from '../../stores/Form/FormsStore';
import {ContextArrayType, ContextMenuTextColor} from '../../features/Form/consts/formContextMenu';

import styles from './styles.module.scss';

type Props = {
    width?: number;
    zIndex?: number;
    menuData: ContextArrayType;
    portalSelector?: string;
};

export const renderSubMenu = (subMenu: ContextArrayType) => {
    return (
        subMenu &&
        subMenu.map(subItem => (
            <ContextMenuItem
                onClick={subItem.onClick}
                divider={subItem.divider}
                key={subItem.name}
                isRTL={isRTL()}
                shortCode={subItem.shortCode}
                componentClasses={cx('new-ui-theme', styles.contextItem)}
                icon={
                    <Icon
                        componentClasses={styles.contextIconColor}
                        icon={isRTL() ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight}
                    />
                }
            >
                <Text startPadding textColor={subItem.color || ContextMenuTextColor.black}>
                    {subItem.name}
                </Text>
            </ContextMenuItem>
        ))
    );
};

export const useContextMenu = ({width = 200, zIndex = 15000, menuData, portalSelector}: Props) => {
    const [position, setPosition] = useState({});
    const [isOpen, setOpen] = useState(false);
    const {Portal} = usePortal({
        ...(portalSelector && {bindTo: document.getElementById(portalSelector)})
    });

    const openContextMenu = useCallback(
        async (anchor: HTMLElement, x: number, y: number) => {
            if (anchor && !isOpen) {
                setPosition({
                    position: 'fixed',
                    top: y,
                    left: x,
                    right: 'auto',
                    zIndex,
                    width
                });
                setOpen(true);
            }
        },
        [width, zIndex, isOpen]
    );

    const closeContextMenu = useCallback(() => {
        if (isOpen) {
            setOpen(false);
            const currentForm = formsStore.getActiveForm();
            currentForm?.formActiveElement?.focus();
        }
    }, [isOpen]);

    const contextMenuComponent = useMemo(() => {
        return (
            <Portal>
                <div className="reactComp">
                    {isOpen && (
                        <div
                            style={{zIndex}}
                            className={styles.contextMenuBackdrop}
                            onClick={closeContextMenu}
                            onContextMenu={closeContextMenu}
                        />
                    )}

                    <ContextMenu
                        isRTL={isRTL()}
                        dropdownStyle={position}
                        isOpen={isOpen}
                        onClose={closeContextMenu}
                        componentClasses={styles.dropdown}
                    >
                        {menuData.map((item, idx) => (
                            <ContextMenuItem
                                onClick={item.onClick}
                                key={idx}
                                isRTL={isRTL()}
                                shortCode={item.shortCode}
                                subMenu={renderSubMenu(item.subMenu)}
                                componentClasses={cx('new-ui-theme', styles.contextItem)}
                                dropdownWidth={200}
                                icon={
                                    <Icon
                                        componentClasses={styles.contextIcon}
                                        icon={
                                            isRTL()
                                                ? PriorityIcon.arrowLeft
                                                : PriorityIcon.arrowRight
                                        }
                                    />
                                }
                            >
                                <Text
                                    startPadding
                                    textColor={item.color || ContextMenuTextColor.black}
                                >
                                    {item.name}
                                </Text>
                            </ContextMenuItem>
                        ))}
                    </ContextMenu>
                </div>
            </Portal>
        );
    }, [Portal, isOpen, menuData, position, zIndex, closeContextMenu]);

    return {openContextMenu, closeContextMenu, contextMenuComponent};
};
